import { feishuLogin as feishuLoginApi, getUserInfo, getUserPage, doLoginByTicket, login } from '@/api/user';
import { getToken, removeToken, setToken } from '@/utils/auth';
import { resetRouter } from '@/router';
import { setHttpTurnHttps } from '@/utils/tools';
import { Loading, Message } from 'element-ui';

const getDefaultState = () => {
  return {
    token: getToken(),
    name: '',
    avatar: '',
    // 页面路由权限
    roles: [],
    // 按钮级权限
    buttonPermissions: [],
    id: '', // 用户ID
    code: '', // 工号
    username: '',
    hasAudit: 0, // 当前用户是否可以审核工单 0:没有资格 1:可以审核
    adminRole: false, // 当前用户是否为管理员角色（后端按角色名判定，中文/葡语都算）
    realName: '', // 用户真实名字
    siteId: '', // 站点
    siteUserType: '', // 站点类型
    dotName: '', // 网点名-——截图用
    siteCode: '', // 网点编码
    orgId: '', // 部门id
    orgCode: '', // 部门code
    platformCode: '', // 所属平台
    managerList: [], // 经办人列表
    passwordLastModified: '', // 用户上次修改密码时间
    getmessageNumber: 0, // 系统消息数量
    getmessageShowObj: {}, // 控制是否显示系统消息
    siteName: ''
  };
};

const state = getDefaultState();

const mutations = {
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ID: (state, id) => {
    state.id = id;
  },
  SET_CODE: (state, code) => {
    state.code = code;
  },
  SET_USER_NAME: (state, username) => {
    state.username = username;
  },
  SET_TOKEN: (state, token) => {
    state.token = token;
  },
  SET_NAME: (state, name) => {
    state.name = name;
  },
  SET_REAL_NAME: (state, realName) => {
    state.realName = realName;
  },
  SET_AVATAR: (state, avatar) => {
    state.avatar = avatar;
  },
  SET_ROLES: (state, menuPermissions) => {
    state.roles = menuPermissions;
  },
  SET_BUTTONPERMISSIONS: (state, buttonPermissions) => {
    state.buttonPermissions = buttonPermissions;
  },
  SET_AUDIT: (state, hasAudit) => {
    state.hasAudit = hasAudit;
  },
  SET_ADMINROLE: (state, adminRole) => {
    state.adminRole = adminRole;
  },
  SET_REALNAME: (state, realName) => {
    state.realName = realName;
  },
  SET_SITEID: (state, siteId) => {
    state.siteId = siteId;
  },
  SET_SITENAME: (state, siteName) => {
    state.siteName = siteName;
  },
  SET_SITEUSERTYPE: (state, siteUserType) => {
    state.siteUserType = siteUserType;
  },
  SET_DOTNAME: (state, dotName) => {
    state.dotName = dotName;
  },
  SET_SITECODE: (state, siteCode) => {
    state.siteCode = siteCode;
  },
  SET_ORGID: (state, orgId) => {
    state.orgId = orgId;
  },
  SET_PLATFORMCODE: (state, platformCode) => {
    state.platformCode = platformCode;
  },
  // 经办人列表赋值
  SET_MANAGERLIST: (state, data) => {
    state.managerList = data;
  },
  SET_PASSWORDLASTMODIFIED: (state, passwordLastModified) => {
    state.passwordLastModified = passwordLastModified;
  },
  // 部门code
  SET_ORG_CODE: (state, orgCode) => {
    state.orgCode = orgCode;
  },
  // 系统消息数量
  set_getmessageNumber: (state, getmessageNumber) => {
    state.getmessageNumber = getmessageNumber;
  },
  set_getmessageShow: (state, getmessageShowObj) => {
    state.getmessageShowObj = getmessageShowObj;
  }
};

const actions = {
  // user login
  login({ commit }, userInfo) {
    // const { customerName, password } = userInfo;
    const { username } = userInfo;
    return new Promise((resolve, reject) => {
      login({ ...userInfo, username: username.trim() })
        .then((response) => {
          const { data } = response;
          if (data.status === 'ERROR') {
            Message({
              message: data.msg,
              type: 'error'
            });
          }
          commit('SET_TOKEN', data.token);
          setToken(data.token);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // feishu login
  feishuLogin({ commit }, code) {
    return new Promise((resolve, reject) => {
      feishuLoginApi(code, window.location.origin)
        .then((response) => {
          // 处理 code 301 的情况（未绑定账号）
          if (response.code === 301) {
            // 拒绝 promise，传递完整的错误信息
            const error = new Error(response.msg || '账号未绑定');
            error.code = 301;
            error.data = response.data;
            error.response = { data: response };
            reject(error);
            return;
          }
          // 处理正常登录
          if (response.code === 200 || response.data) {
            const data = response.data || response;
            const token = data.token || data.data?.token;
            if (token) {
              setToken(token);
              commit('SET_TOKEN', token);
            }
            resolve(data);
          } else {
            reject(new Error(response.msg));
          }
        })
        .catch((error) => {
          // 如果是 axios 错误，检查是否是 code 301
          if (error.response && error.response.data && error.response.data.code === 301) {
            // 重新抛出，让调用方处理
            reject(error);
          } else {
            reject(error);
          }
        });
    });
  },

  // sso login
  ssoLogin({ commit }, ticket) {
    return new Promise((resolve, reject) => {
      doLoginByTicket(ticket)
        .then((response) => {
          const { data } = response;
          if (data.status === 'ERROR') {
            Message({
              message: data.msg,
              type: 'error'
            });
            reject(data.msg);
            return;
          }
          commit('SET_TOKEN', data.token);
          setToken(data.token);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // get user info
  getInfo({ commit }) {
    return new Promise((resolve, reject) => {
      getUserInfo()
        .then((response) => {
          const { data } = response;
          if (!data) {
            reject('验证失败，请重新登录。');
          }
          const {
            username,
            avatar,
            permission,
            // menuPermissions,
            // buttonPermissions,
            id,
            code,
            hasAudit,
            adminRole,
            realName,
            siteId,
            siteUserType,
            siteName,
            siteCode,
            orgId,
            orgCode,
            platformCode,
            passwordLastModified
          } = data;
          // debugger;
          const menuPermissions = permission.menuPermissions;
          const buttonPermissions = permission.buttonPermissions;
          // 默认添加主页权限,防止死循环
          menuPermissions.push('dashboard');
          commit('SET_ROLES', menuPermissions);

          commit('SET_ID', id); // 当前用户ID
          commit('SET_CODE', code);
          commit('SET_USER_NAME', username); // 工号

          commit('SET_AUDIT', hasAudit);
          commit('SET_ADMINROLE', adminRole === true);
          commit('SET_REALNAME', realName);
          commit('SET_SITEID', siteId);
          commit('SET_SITENAME', siteName);
          commit('SET_SITEUSERTYPE', siteUserType);
          commit('SET_DOTNAME', siteName);
          commit('SET_SITECODE', siteCode);

          commit('SET_BUTTONPERMISSIONS', buttonPermissions);
          commit('SET_NAME', username);
          commit('SET_REAL_NAME', realName);
          commit('SET_ORGID', orgId);
          commit('SET_ORG_CODE', orgCode);
          commit('SET_PLATFORMCODE', platformCode);
          commit('SET_PASSWORDLASTMODIFIED', passwordLastModified);
          commit(
            'SET_AVATAR',
            setHttpTurnHttps(avatar) ||
              'https://wpimg.wallstcn.com/f778738c-e4f8-4870-b634-56703b4acafe.gif'
          );
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // user logout
  logout({ commit }) {
    return new Promise((resolve) => {
      // logout(state.token).then(() => {
      removeToken(); // must remove  token  first
      resetRouter();
      commit('RESET_STATE');
      // 退出登录时一并清空动态权限路由，避免下个账号复用上个账号的菜单状态
      commit('permission/RESET_STATE', null, { root: true });
      resolve();
      // }).catch(error => {
      //   reject(error)
      // })
    });
  },

  // remove token
  resetToken({ commit }) {
    return new Promise((resolve) => {
      removeToken(); // must remove  token  first
      commit('RESET_STATE');
      // token 失效后同步重置权限模块，防止内存里残留旧的动态路由
      commit('permission/RESET_STATE', null, { root: true });
      resolve();
    });
  },

  // dynamically modify permissions
  async changeRoles({ commit, dispatch }, role) {
    const token = role + '-token';

    commit('SET_TOKEN', token);
    setToken(token);

    const { roles } = await dispatch('getInfo');

    resetRouter();

    // generate accessible routes map based on roles
    const accessRoutes = await dispatch('permission/generateRoutes', roles, { root: true });
    // dynamically add accessible routes
    this.$router.addRoutes(accessRoutes);

    // reset visited views and cached views
    dispatch('tagsView/delAllViews', null, { root: true });
  },
  // 获取经办人列表
  getManagerList({ commit, dispatch }, name) {
    console.log('被调用了', name);
    // 获取操作员
    const param = {
      pageNumber: 1,
      pageSize: 10,
      params: {
        realName: '',
        accountState: '1'
      }
    };
    if (name) {
      param.params.realName = name;
    }
    return new Promise((resolve, reject) => {
      const loading = Loading.service({
        fullscreen: true,
        lock: true,
        spinner: 'el-icon-loading',
        text: 'loading...',
        background: 'rgba(250, 250, 250, 0.6)'
      });
      getUserPage(param)
        .then((res) => {
          loading.close();
          if (res.code === 200) {
            commit('SET_MANAGERLIST', res.data.records);
            resolve();
          }
        })
        .catch((err) => {
          console.error(err);
          loading.close();
          reject(err);
        });
    });
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
